import React, { useState, useEffect } from 'react';
import { Settings, Save, RotateCcw, Info } from 'lucide-react';
import { Program } from '../../types/ltss';

// Program limit configuration
export interface ProgramLimitConfig {
  maxHoursPerDay: number;
  maxUnitsPerDay: number;
  unitsPerHour: number;
  ratePerUnit: number | null; // null = auto-derive from data
}

export interface ProgramLimitsConfig {
  [key: string]: ProgramLimitConfig;
}

// Default limits based on Maryland HCBS programs
export const DEFAULT_PROGRAM_LIMITS: ProgramLimitsConfig = {
  CFC: {
    maxHoursPerDay: 12,
    maxUnitsPerDay: 48,
    unitsPerHour: 4,
    ratePerUnit: null,
  },
  CO: {
    maxHoursPerDay: 12,
    maxUnitsPerDay: 48,
    unitsPerHour: 4,
    ratePerUnit: null,
  },
  CPAS: {
    maxHoursPerDay: 12,
    maxUnitsPerDay: 48,
    unitsPerHour: 4,
    ratePerUnit: null,
  },
  EPSDT: {
    maxHoursPerDay: 24, // Private Duty Nursing - can be 24hr
    maxUnitsPerDay: 96,
    unitsPerHour: 4,
    ratePerUnit: null,
  },
  REM: {
    maxHoursPerDay: 24, // Residential - can be 24hr
    maxUnitsPerDay: 96,
    unitsPerHour: 4,
    ratePerUnit: null,
  },
};

const PROGRAM_LABELS: Record<Program, string> = {
  CFC: 'Community First Choice (CFC)',
  CO: 'Community Options (CO)',
  CPAS: 'Community Personal Assistance Services (CPAS)',
  EPSDT: 'Early Periodic Screening (EPSDT/PDN)',
  REM: 'Residential (REM)',
};

const STORAGE_KEY = 'ltss_program_limits';

interface ProgramLimitsProps {
  onSave?: (config: ProgramLimitsConfig) => void;
}

export function ProgramLimits({ onSave }: ProgramLimitsProps) {
  const [config, setConfig] = useState<ProgramLimitsConfig>(DEFAULT_PROGRAM_LIMITS);
  const [hasChanges, setHasChanges] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load saved config
  useEffect(() => {
    const savedConfig = localStorage.getItem(STORAGE_KEY);
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig);
        // Merge with defaults to ensure all programs exist
        setConfig({ ...DEFAULT_PROGRAM_LIMITS, ...parsed });
      } catch (e) {
        console.error('Failed to load program limits:', e);
      }
    }
  }, []);

  const handleChange = (program: string, field: keyof ProgramLimitConfig, value: number | null) => {
    setConfig(prev => ({
      ...prev,
      [program]: {
        ...prev[program],
        [field]: value,
        // Auto-calculate units when hours change
        ...(field === 'maxHoursPerDay' && value !== null ? {
          maxUnitsPerDay: value * (prev[program]?.unitsPerHour || 4)
        } : {}),
        // Auto-calculate hours when units change
        ...(field === 'maxUnitsPerDay' && value !== null ? {
          maxHoursPerDay: value / (prev[program]?.unitsPerHour || 4)
        } : {}),
      }
    }));
    setHasChanges(true);
    setSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    setHasChanges(false);
    setSaved(true);
    onSave?.(config);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    setConfig(DEFAULT_PROGRAM_LIMITS);
    setHasChanges(true);
  };

  const programs: Program[] = ['CFC', 'CO', 'CPAS', 'EPSDT', 'REM'];

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Settings className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Program Billing Limits</h3>
            <p className="text-sm text-slate-400">
              Set maximum billable hours/units per day for each program
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-400 
                     hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className={`flex items-center gap-1 px-4 py-1.5 text-sm font-medium rounded-lg transition-colors
              ${hasChanges 
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : saved 
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-slate-700 text-slate-400 cursor-not-allowed'
              }`}
          >
            <Save className="w-4 h-4" />
            {saved ? 'Saved!' : 'Save'}
          </button>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-6">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-300">
            These limits cap the billable hours per staff per day. Hours worked beyond the limit 
            will be tracked but not counted toward billable totals. Rate per unit is auto-calculated 
            from your data unless manually set.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {programs.map(program => (
          <div 
            key={program}
            className="bg-slate-800/50 rounded-lg p-4 border border-slate-700"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-sm font-medium text-white">{program}</h4>
                <p className="text-xs text-slate-500">{PROGRAM_LABELS[program]}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Max Hours/Day</label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  value={config[program]?.maxHoursPerDay || 12}
                  onChange={(e) => handleChange(program, 'maxHoursPerDay', parseFloat(e.target.value) || 12)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                           text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-xs text-slate-400 mb-1">Max Units/Day</label>
                <input
                  type="number"
                  min="1"
                  max="96"
                  value={config[program]?.maxUnitsPerDay || 48}
                  onChange={(e) => handleChange(program, 'maxUnitsPerDay', parseInt(e.target.value) || 48)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                           text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-xs text-slate-400 mb-1">Units/Hour</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={config[program]?.unitsPerHour || 4}
                  onChange={(e) => handleChange(program, 'unitsPerHour', parseInt(e.target.value) || 4)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                           text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-xs text-slate-400 mb-1">Rate/Unit ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Auto"
                  value={config[program]?.ratePerUnit ?? ''}
                  onChange={(e) => handleChange(program, 'ratePerUnit', e.target.value ? parseFloat(e.target.value) : null)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                           text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500
                           placeholder:text-slate-500"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper to get program limits from localStorage
export function getProgramLimits(): ProgramLimitsConfig {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    try {
      return { ...DEFAULT_PROGRAM_LIMITS, ...JSON.parse(saved) };
    } catch (e) {
      console.error('Failed to load program limits:', e);
    }
  }
  return DEFAULT_PROGRAM_LIMITS;
}

// Helper to calculate billable hours respecting program limits
export function calculateBillableHours(
  actualHours: number,
  program: Program,
  limits: ProgramLimitsConfig = getProgramLimits()
): number {
  const programLimit = limits[program] || DEFAULT_PROGRAM_LIMITS[program];
  return Math.min(actualHours, programLimit?.maxHoursPerDay || 12);
}

// Helper to calculate billable units respecting program limits
export function calculateBillableUnits(
  actualHours: number,
  program: Program,
  limits: ProgramLimitsConfig = getProgramLimits()
): number {
  const programLimit = limits[program] || DEFAULT_PROGRAM_LIMITS[program];
  const billableHours = Math.min(actualHours, programLimit?.maxHoursPerDay || 12);
  return billableHours * (programLimit?.unitsPerHour || 4);
}

// Helper to derive rate per unit from data
export function deriveRatePerUnit(
  totalPaid: number,
  totalUnitsBilled: number,
  program: Program,
  limits: ProgramLimitsConfig = getProgramLimits()
): number {
  const programLimit = limits[program] || DEFAULT_PROGRAM_LIMITS[program];
  
  // If manually set, use that
  if (programLimit?.ratePerUnit !== null && programLimit?.ratePerUnit !== undefined) {
    return programLimit.ratePerUnit;
  }
  
  // Otherwise derive from data
  if (totalUnitsBilled > 0) {
    return totalPaid / totalUnitsBilled;
  }
  
  return 0;
}
